'use client';

import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '@/lib/auth';
import type { CreditUnitMetadata } from '@/lib/credits/unit';
import { normalizeApiError } from '@/lib/api/types';
import { translateClient } from '@/lib/i18n/client';
import { useCreditsStore } from '@/lib/stores/credits-store';

/**
 * 用户积分信息 - 简化版
 * credits_balance 是唯一的真实余额
 */
export interface UserCredits {
  credits_balance: number;           // 当前可用积分 (subscription + purchased)
  free_credit_policy?: 'daily' | 'welcome_once';
  welcome_credit_amount?: number;
  welcome_credit_status?: 'pending' | 'granted' | 'legacy_satisfied' | 'ineligible' | null;
  welcome_credit_granted_at?: string | null;
  subscription_credits_remaining: number; // 订阅当期积分
  subscription_credits_expires_at?: string | null; // 订阅积分到期时间 (UTC)
  purchased_credits: number;         // 充值积分 (永不过期)
  tier: 'free' | 'pro' | 'enterprise';  // 会员等级
  credits_total_granted?: number;    // 累计获得 (统计用)
  credits_total_consumed?: number;   // 累计消耗 (统计用)
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
}

/**
 * 模型定价信息
 */
export interface ModelPricing {
  model_key: string;
  model_name: string;
  provider: string;
  category: string;
  description: string | null;
  pricing_type: 'per_call' | 'per_second' | 'per_minute' | 'fixed';
  credits_rate: number;
  min_credits: number;
  max_credits: number | null;
}

/**
 * 交易记录
 */
export interface CreditTransaction {
  id: string;
  transaction_type: 'consume' | 'grant' | 'refund' | 'purchase' | 'expire' | 'adjust' | 'deduct' | 'hold';
  status: 'pending' | 'completed' | 'cancelled';
  credits_amount: number;
  credits_before: number;
  credits_after: number;
  model_key: string | null;
  model_name: string | null;
  ai_task_id: string | null;
  description: string | null;
  created_at: string;
  description_key?: 'welcome_credit_grant' | 'registration_credit_grant' | 'subscription_tier_expiry' | 'incident_source_contract_compensation' | 'canvas_agent_text' | 'credits_reserved' | 'reservation_closed' | 'consumption_reversed' | 'credits_consumed' | 'reservation_partially_settled' | 'credits_refunded' | 'credits_expired' | null;
  resolution?: string | null;
  settlement?: string | null;
  settlement_detail?: { consumed: number; refunded: number; expired: number } | null;
}

export interface CreditEstimate {
  credits_required: number;
  estimate_min: number;
  estimate_max: number;
  credit_unit_mode?: 'dobi' | string;
  unit_key: 'dobi_coin' | string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
  pricing_mode?: string;
}

async function authJsonFetch<T>(input: string, init?: RequestInit): Promise<T> {
  const response = await authFetch(input, init);

  if (!response.ok) {
    throw response;
  }

  return response.json() as Promise<T>;
}

/**
 * useCredits Hook
 * 管理用户积分状态 - 使用全局 store 实现跨组件同步
 */
export function useCredits(options: { autoInit?: boolean } = {}) {
  const autoInit = options.autoInit !== false;
  // 使用全局 store
  const { credits, loading, error, initCredits, refetchCredits } = useCreditsStore();

  useEffect(() => {
    if (!autoInit) return;
    // 初始化积分（内部会判断是否已加载，不会重复请求）
    initCredits();
  }, [autoInit, initCredits]);

  /**
   * 计算操作所需积分
   */
  const calculateCredits = useCallback(async (
    modelKey: string,
    params?: { duration_seconds?: number; duration_minutes?: number; count?: number }
  ): Promise<CreditEstimate> => {
    return authJsonFetch('/api/credits/calculate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model_key: modelKey,
        ...params,
      }),
    });
  }, []);

  /**
   * 检查积分是否充足
   */
  const checkCredits = useCallback(async (
    creditsRequired: number
  ): Promise<{ allowed: boolean; message: string }> => {
    try {
      return await authJsonFetch('/api/credits/check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credits_required: creditsRequired,
        }),
      });
    } catch {
      return { allowed: false, message: translateClient('errors', 'contexts.credits.unauthorized') };
    }
  }, []);

  /**
   * 消耗积分
   */
  const consumeCredits = useCallback(async (
    modelKey: string,
    credits: number,
    options?: { ai_task_id?: string; description?: string }
  ): Promise<{ success: boolean; credits_after: number }> => {
    const response = await authFetch('/api/credits/consume', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model_key: modelKey,
        credits,
        ...options,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw normalizeApiError({ code: 'UNAUTHORIZED' }, translateClient('errors', 'contexts.credits.unauthorized'));
      }
      if (response.status === 402) {
        throw normalizeApiError(error, translateClient('errors', 'contexts.credits.insufficientCredits'));
      }
      throw normalizeApiError(error, translateClient('errors', 'contexts.credits.unknown'));
    }

    const result = await response.json();
    
    // 刷新积分
    await refetchCredits();
    
    return result;
  }, [refetchCredits]);

  /**
   * 冻结积分 (任务开始时)
   */
  const holdCredits = useCallback(async (
    credits: number,
    aiTaskId: string,
    modelKey?: string
  ): Promise<{ success: boolean; transaction_id: string }> => {
    const response = await authFetch('/api/credits/hold', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credits,
        ai_task_id: aiTaskId,
        model_key: modelKey,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw normalizeApiError({ code: 'UNAUTHORIZED' }, translateClient('errors', 'contexts.credits.unauthorized'));
      }
      if (response.status === 402) {
        throw normalizeApiError(error, translateClient('errors', 'contexts.credits.insufficientCredits'));
      }
      throw normalizeApiError(error, translateClient('errors', 'contexts.credits.unknown'));
    }

    const result = await response.json();
    await refetchCredits();
    return result;
  }, [refetchCredits]);

  return {
    credits,
    loading,
    error,
    refetch: refetchCredits,
    calculateCredits,
    checkCredits,
    consumeCredits,
    holdCredits,
  };
}

/**
 * useModelPricing Hook
 * 获取模型定价信息
 */
export function useModelPricing(category?: string) {
  const [pricing, setPricing] = useState<ModelPricing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const url = category 
          ? `/api/credits/pricing/public?category=${category}`
          : '/api/credits/pricing/public';
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('获取定价失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, [category]);

  return { pricing, loading };
}

/**
 * useCreditTransactions Hook
 * 获取交易记录
 */
export function useCreditTransactions(limit = 50, enabled = true) {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchTransactions = useCallback(async (offset = 0) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch(
        `/api/credits/transactions?limit=${limit}&offset=${offset}`,
        { method: 'GET' }
      );

      if (response.ok) {
        const data = await response.json();
        if (offset === 0) {
          setTransactions(data.transactions);
        } else {
          setTransactions(prev => [...prev, ...data.transactions]);
        }
        setTotal(data.total);
        setHasMore(data.has_more);
      } else {
        setError(translateClient('errors', 'contexts.credits.unknown'));
      }
    } catch (err) {
      console.error('获取交易记录失败:', err);
      setError(translateClient('errors', 'contexts.credits.unknown'));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchTransactions(0);
  }, [enabled, fetchTransactions]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchTransactions(transactions.length);
    }
  }, [loading, hasMore, transactions.length, fetchTransactions]);

  return {
    transactions,
    total,
    loading,
    error,
    hasMore,
    loadMore,
    refetch: () => fetchTransactions(0),
  };
}
